// src/StageReport.js
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectAllControls } from './redux/selectors/assessmentSelectors';
import {
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Button
} from '@mui/material';
import ExpandMoreIcon   from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

function StageReport({ revision = {}, entries = [], from, to, modelId, onClose }) {
  /* ---------- normalise revision ---------- */
  const newRisks     = Array.isArray(revision.newRisks)    ? revision.newRisks    : [];
  const updateRisks  = Array.isArray(revision.updateRisks) ? revision.updateRisks : [];
  const newCtrlsRaw = React.useMemo(
       () => (Array.isArray(revision.newCtrls) ? revision.newCtrls : []),
       [revision.newCtrls]
     );
    
     const updCtrlsRaw = React.useMemo(
       () => (Array.isArray(revision.updateCtrls) ? revision.updateCtrls : []),
       [revision.updateCtrls]
     );

  /* ---------- look‑ups ---------- */
  // riskId  -> entry (to get current title / section / category)
  const entryMap = useMemo(() => {
    const m = {};
    entries.forEach(e => { m[e.riskId] = e; });
    return m;
  }, [entries]);

  // controlId -> full control object (savedControls slice)
  const allControls = useSelector(selectAllControls);

  /* ---------- group controls ---------- */
  // 1.  newCtrls are keyed by `new risk title`
  const newCtrlsByRisk = useMemo(() => {
    const m = {};
    newCtrlsRaw.forEach(c => {
      const key = c['new risk title'];
      if (!key) return;
      if (!m[key]) m[key] = [];
      m[key].push(c);
    });
    return m;
  }, [newCtrlsRaw]);

  // 2.  updated controls belong to existing riskIds
  const updCtrlsByRisk = useMemo(() => {
    const m = {};
    updCtrlsRaw.forEach(c => {
      const control = allControls[c.controlId];
      if (!control) return;
      const rid = control.riskId;
      if (!m[rid]) m[rid] = [];
      m[rid].push({ ...c, name: control.name });
    });
    return m;
  }, [updCtrlsRaw, allControls]);

  /* ===================================================================== */
  return (
    <Box p={2}>
      <Button sx={{ mb: 2 }} onClick={onClose}>← Back</Button>

      <Typography variant="h5" gutterBottom>
        Revision Report&nbsp;&nbsp;|&nbsp;&nbsp;{from} → {to}
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {/* ─────────────────────  NEW  RISKS  ───────────────────── */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">
            New Risks&nbsp;({newRisks.length})
          </Typography>
        </AccordionSummary>

        <AccordionDetails>

          {newRisks.length === 0 && (
            <Typography color="text.secondary">No new risks identified.</Typography>
          )}

          {newRisks.map((r, idx) => {
            const controls = newCtrlsByRisk[r.title] || [];
            return (
              <Paper key={idx} elevation={0} sx={{ mb: 3, p: 2 }}>
                <Typography variant="subtitle2">
                  {r.title}{' '}
                  <Tooltip title={r.reason} arrow>
                    <InfoOutlinedIcon fontSize="inherit" sx={{ verticalAlign:'text-top' }}/>
                  </Tooltip>
                </Typography>

                {controls.length > 0 ? (
                  <TableContainer sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Suggestion</TableCell>
                          <TableCell>Info</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {controls.map((c,i)=>(
                          <TableRow key={i}>
                            <TableCell>{c.name}</TableCell>
                            <TableCell>{c.suggestion}</TableCell>
                            <TableCell>
                              <Tooltip title={c.reason} arrow>
                                <InfoOutlinedIcon fontSize="small"/>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    No recommended actions for this risk.
                  </Typography>
                )}
              </Paper>
            );
          })}
        </AccordionDetails>
      </Accordion>

      {/* ────────────────────  UPDATED  RISKS  ─────────────────── */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">
            Updated Risks&nbsp;({updateRisks.length})
          </Typography>
        </AccordionSummary>

        <AccordionDetails>

          {updateRisks.length === 0 && (
            <Typography color="text.secondary">No updated risks.</Typography>
          )}

          {updateRisks.map((ur) => {
            const entry    = entryMap[ur.riskId];
            const ctrls    = updCtrlsByRisk[ur.riskId] || [];

            return (
              <Paper key={ur.riskId} elevation={0} sx={{ mb: 3, p: 2 }}>
                <Typography variant="subtitle2">
                  {entry?.title || '(unknown risk)'}{' '}
                  <Tooltip title={ur.reason} arrow>
                    <InfoOutlinedIcon fontSize="inherit" sx={{ verticalAlign:'text-top' }}/>
                  </Tooltip>
                </Typography>

                {ctrls.length > 0 ? (
                  <TableContainer sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Suggestion</TableCell>
                          <TableCell>Info</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ctrls.map((c) => (
                          <TableRow key={c.controlId}>
                            <TableCell>{c.name}</TableCell>
                            <TableCell>{c.suggestion}</TableCell>
                            <TableCell>
                              <Tooltip title={c.reason} arrow>
                                <InfoOutlinedIcon fontSize="small"/>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    Risk flagged for control update, but no controls were returned.
                  </Typography>
                )}
              </Paper>
            );
          })}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default StageReport;